import { useMemo, useCallback, useEffect } from "react"
import { useLocalization } from "@tm/localization"
import { Dictionary } from "@tm/morpheus"
import { channel } from "@tm/models"
import { useWorkTask } from "@tm/context-distribution"
import { encodeUniqueId, renderRoute, uniqueId, useMultiWidgetState } from "@tm/utils"
import { Tab, Tabs, TabsProps } from "@tm/components"
import { useHistory, useParams } from "react-router"

export type TabItemData = {
    text: string
    sort?: number
    path?: string
}

type ItemData = TabItemData & { key: string }

type Props = {
    storeId?: string
    items: Record<string, TabItemData>
    hideItems?: Dictionary<boolean>
    layout?: TabsProps["layout"]
    selectedItem?: string
}

const vehiclePartsKey = "VEHICLE_PARTS"
const partsIndicatorKey = "PARTS_INDICATOR"

export function TabControl(props: Props) {
    const { items, hideItems, storeId, selectedItem, layout } = props

    const { translateText, isTranslationId } = useLocalization()
    const { workTask, workTaskId } = useWorkTask() || {}
    const history = useHistory()
    const matchParams = useParams()
    const [selectedItemState, setSelectedItemState] = useMultiWidgetState({ storeId })

    const isButtonLayout = layout === "buttons"
    const existingVehicleId = workTask?.vehicle?.id

    const options = useMemo<ItemData[]>(() => {
        if (!items) {
            return []
        }

        const result: ItemData[] = Object.entries(items).map(([dictionaryKey, value]) => {
            return { ...value, key: dictionaryKey }
        })

        return result.filter((x) => !!x && !(hideItems && Object.keys(hideItems).includes(x.key))).orderBy((x) => x.sort || 0)
    }, [items, hideItems])

    const initialItem = useMemo(() => {
        if (selectedItem) {
            const optionsKey = options.find((item) => item.key === selectedItem)?.key
            if (optionsKey) {
                return optionsKey
            }
        }
        return options[0].key
    }, [selectedItem, options])

    const isDisabled = useCallback(
        (key: string) => {
            return key === vehiclePartsKey && !workTask?.vehicle
        },
        [workTask?.vehicle]
    )

    const onSelectionChange = (key: string) => {
        const tempItem = options.find((item) => item.key === key)
        if (key === "EXTERN" || tempItem?.path) {
            // Open External module
            setSelectedItemState(initialItem)
            if (tempItem?.path) {
                const workTaskId = workTask?.id || uniqueId()
                const url = renderRoute(tempItem?.path, { ...matchParams, workTaskId: encodeUniqueId(workTaskId) })
                history.push(url)
            }
        } else {
            setSelectedItemState(key)
        }
    }

    useEffect(() => {
        // if the selectedItem is disabled open the initial item
        if (isDisabled(selectedItemState) && selectedItemState !== initialItem) {
            setSelectedItemState(initialItem)
        }
    }, [selectedItemState, isDisabled, initialItem])

    useEffect(() => {
        if (workTaskId) {
            if (existingVehicleId && options.some((option) => option.key === vehiclePartsKey)) {
                setSelectedItemState(vehiclePartsKey)
            } else {
                setSelectedItemState(initialItem)
            }
        }
        return channel("WORKTASK", workTaskId).subscribe("PARTS/SHOW_PARTS_INDICATOR", () => {
            if (options.some((item) => item.key === partsIndicatorKey)) {
                setSelectedItemState(partsIndicatorKey)
            }
        })
    }, [options, workTaskId, existingVehicleId])

    useEffect(() => {
        if (options) {
            setSelectedItemState(initialItem)
        }
        return channel("APP").subscribe("SHORTCUTS", (rez) => {
            if (["UNI", "DIRECT"].includes(rez.key) && options.some((option) => option.key === rez.key)) {
                setSelectedItemState(rez.key)
            }
        })
    }, [options])

    return (
        <Tabs
            layout={layout}
            variant="scrollable"
            value={selectedItemState}
            onChange={(_, val) => onSelectionChange(val)}
            size={isButtonLayout ? "large" : undefined}
            sx={{ mb: isButtonLayout ? 1 : 2 }}
        >
            {options.map((item) => (
                <Tab
                    key={item.key}
                    label={isTranslationId(item.text) ? translateText(item.text) : item.text}
                    value={item.key}
                    disabled={isDisabled(item.key)}
                />
            ))}
        </Tabs>
    )
}
